//-- copyright
// OpenProject is an open source project management software.
// Copyright (C) the OpenProject GmbH
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License version 3.
//
// OpenProject is a fork of ChiliProject, which is a fork of Redmine. The copyright follows:
// Copyright (C) 2006-2013 Jean-Philippe Lang
// Copyright (C) 2010-2013 the ChiliProject Team
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License
// as published by the Free Software Foundation; either version 2
// of the License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program; if not, write to the Free Software
// Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
//
// See COPYRIGHT and LICENSE files for more details.
//++

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { I18nService } from 'core-app/core/i18n/i18n.service';
import { DebouncedEventEmitter } from 'core-app/shared/helpers/rxjs/debounced-event-emitter';
import { trackByName } from 'core-app/shared/helpers/angular/tracking-functions';
import { WorkPackageViewFiltersService } from 'core-app/features/work-packages/routing/wp-view-base/view-services/wp-view-filters.service';
import { WorkPackageFiltersService } from 'core-app/features/work-packages/components/filters/wp-filters/wp-filters.service';
import { UntilDestroyedMixin } from 'core-app/shared/helpers/angular/until-destroyed.mixin';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { QueryFilterInstanceResource } from 'core-app/features/hal/resources/query-filter-instance-resource';
import { QueryFilterResource } from 'core-app/features/hal/resources/query-filter-resource';
import { WorkPackageViewBaselineService } from 'core-app/features/work-packages/routing/wp-view-base/view-services/wp-view-baseline.service';
import { combineLatestWith } from 'rxjs';

const ADD_FILTER_SELECT_INDEX = -1;

// The filter-by-text box is not one of the query's filters - it has no operator
// and no instance of its own - but it is one of the names in the left column,
// so it needs an id to be selected by.
const SEARCH_PANE_ID = '__search__';

@Component({
  selector: 'op-query-filters',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './query-filters.component.html',
})
export class QueryFiltersComponent extends UntilDestroyedMixin implements OnInit, OnChanges {
  @Input() public filters:QueryFilterInstanceResource[];

  @Input() public showCloseFilter = false;

  @Output() public filtersChanged = new DebouncedEventEmitter<QueryFilterInstanceResource[]>(
    componentDestroyed(this),
    500,
  );

  // Reset is not one of the filter edits, so it does not go through
  // filtersChanged: it discards the whole set and reloads the view's default,
  // which the container does by loading the default query.
  @Output() public resetFilters = new EventEmitter<void>();

  public remainingFilters:any[] = [];

  public readonly searchPaneId = SEARCH_PANE_ID;

  // Which name in the left column the right one is showing.
  public selectedFilterId:string = SEARCH_PANE_ID;

  public focusElementIndex = 0;

  public baselineIncompatibleFilters:string[] = [];

  public trackByName = trackByName;

  public text = {
    close_form: this.I18n.t('js.close_form_title'),
    reset_filters: this.I18n.t('js.work_packages.filters.reset'),
    selected_filter_list: this.I18n.t('js.label_selected_filter_list'),
    button_delete: this.I18n.t('js.button_delete'),
    filter_by_text: this.I18n.t('js.work_packages.label_filter_by_text'),
    baseline_warning: this.I18n.t('js.work_packages.filters.baseline_warning'),
  };

  constructor(
    readonly wpTableFilters:WorkPackageViewFiltersService,
    readonly wpTableBaseline:WorkPackageViewBaselineService,
    readonly wpFiltersService:WorkPackageFiltersService,
    readonly I18n:I18nService,
    readonly cdRef:ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit():void {
    this.wpTableFilters.live$()
      .pipe(
        combineLatestWith(this.wpTableBaseline.live$()),
        this.untilDestroyed(),
      )
      .subscribe(([filters]) => {
        if (this.wpTableBaseline.isActive()) {
          this.baselineIncompatibleFilters = this.wpTableBaseline.detectIncompatibleFilters(filters);
        } else {
          this.baselineIncompatibleFilters = [];
        }
        this.cdRef.detectChanges();
      });
  }

  ngOnChanges() {
    this.updateRemainingFilters();
    this.ensureSelection();
  }

  public selectFilter(id:string) {
    this.selectedFilterId = id;
  }

  // Picking a name that has no filter instance yet makes one and opens it. The
  // query is not touched by that on its own: a filter with no values is not
  // complete, and only complete ones are applied.
  public addFilter(filterToBeAdded:QueryFilterResource) {
    if (!filterToBeAdded) { return; }

    const newFilter = this.wpTableFilters.instantiate(filterToBeAdded);
    this.filters.push(newFilter);

    const index = this.currentFilterLength();
    this.updateFilterFocus(index);
    this.updateRemainingFilters();

    this.selectedFilterId = newFilter.id;
    this.filtersChanged.emit(this.filters);
  }

  public moveThroughFilterNames(event:KeyboardEvent) {
    const ids = this.selectableFilterIds();
    const current = ids.indexOf(this.selectedFilterId);
    if (ids.length === 0 || current === -1) { return; }

    let next:number;
    switch (event.key) {
      case 'ArrowDown': next = (current + 1) % ids.length; break;
      case 'ArrowUp': next = (current - 1 + ids.length) % ids.length; break;
      case 'Home': next = 0; break;
      case 'End': next = ids.length - 1; break;
      default: return;
    }

    event.preventDefault();
    this.selectedFilterId = ids[next];
  }

  // Only the names with a pane behind them. The unused filters below them in the
  // column make one when they are clicked, so they are not somewhere the arrow
  // keys should land.
  private selectableFilterIds():string[] {
    return [SEARCH_PANE_ID, ...this.filters.filter((f) => this.isFilterAvailable(f)).map((f) => f.id)];
  }

  // Keeps the right column showing something that is still there - after a
  // filter is removed, or when the panel is first built.
  private ensureSelection() {
    if (this.selectableFilterIds().includes(this.selectedFilterId)) { return; }

    const [, firstFilter] = this.selectableFilterIds();
    this.selectedFilterId = firstFilter || SEARCH_PANE_ID;
  }

  public closeFilter() {
    this.wpFiltersService.toggleVisibility();
  }

  public reset() {
    this.resetFilters.emit();
  }

  public deactivateFilter(removedFilter:QueryFilterInstanceResource) {
    const index = this.filters.indexOf(removedFilter);
    _.remove(this.filters, (f) => f.id === removedFilter.id);

    this.filtersChanged.emit(this.filters);

    this.updateFilterFocus(index);
    this.updateRemainingFilters();
    this.ensureSelection();
  }

  private updateRemainingFilters() {
    this.remainingFilters = _.sortBy(this.wpTableFilters.remainingVisibleFilters(this.filters), 'name');
  }

  private updateFilterFocus(index:number) {
    const activeFilterCount = this.currentFilterLength();

    if (activeFilterCount === 0) {
      this.focusElementIndex = ADD_FILTER_SELECT_INDEX;
    } else {
      const filterIndex = (index < activeFilterCount) ? index : activeFilterCount - 1;
      const filter = this.currentFilterAt(filterIndex);
      this.focusElementIndex = this.filters.indexOf(filter);
    }
  }

  public currentFilterLength() {
    return this.filters.length;
  }

  public currentFilterAt(index:number) {
    return this.filters[index];
  }

  public isFilterAvailable(filter:QueryFilterResource):boolean {
    return (this.wpTableFilters.availableFilters.some((availableFilter) => availableFilter.id === filter.id)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      && !(this.wpTableFilters.hidden.includes(filter.id) || filter.isTemplated()));
  }
}
